import React, { FC, useContext, useEffect, useReducer } from 'react'
import { Card, Popover } from 'antd'
import { BlueConicRecommendationConfiguration } from '@pushly/models/lib/structs/journeys'
import { LayoutRowWithActions } from '../../../../../../components/data-control-row/layout-row-with-actions'
import { BlueConicRecommendedNotificationContext, BlueConicRecommendedNotificationDispatchContext } from '../reducer'
import { CaretDownFilled, CaretUpFilled, ExclamationCircleOutlined } from '@ant-design/icons'
import { BlueConicBoostsEditor } from '../boosts/blueconic-boosts-editor'
import { BlueConicConfigurationDispatchContext, blueConicConfigurationReducer } from './reducer'
import { BlueConicConfigurationContext } from './context'
import { BlueConicFiltersEditor } from '../filters/blueconic-filters-editor'
import { extractFilterRecordMapFromPQuery, parsePQueryFromFilterRecordMap } from '../filters/helpers'
import { DomainDto } from '../../../../../../dtos/domain'
import './blueconic-configuration-editor.scss'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { EditableViewContext } from '@pushly/aqe/lib/components/hoc/editable-view/editable-view-context'

type OrderHandlerProps = {
    onChange: (offset: 1 | -1) => void
    hideUp?: boolean
    hideDown?: boolean
}

const OrderHandler: FC<OrderHandlerProps> = ({ onChange, hideUp, hideDown }) => {
    return (
        <div className="config-order-handler">
            {!hideUp && (
                <span
                    onClick={() => {
                        onChange(-1)
                    }}
                >
                    <CaretUpFilled />
                </span>
            )}
            {!hideDown && (
                <span
                    onClick={() => {
                        onChange(1)
                    }}
                >
                    <CaretDownFilled />
                </span>
            )}
        </div>
    )
}

type BlueConicConfigurationEditorProps = {
    domain: DomainDto
    configuration: { recordId: string; configuration: BlueConicRecommendationConfiguration }
    handleAdd: (config?: BlueConicRecommendationConfiguration) => void
}

export const BlueConicConfigurationEditor: FC<BlueConicConfigurationEditorProps> = ({
    domain,
    configuration,
    handleAdd,
}) => {
    const { recordId, configuration: config } = configuration

    const viewCtx = useContext(EditableViewContext)
    const isReadonly = viewCtx.mode === 'view'

    const recommendationContext = useContext(BlueConicRecommendedNotificationContext)
    const recommendationDispatch = useContext(BlueConicRecommendedNotificationDispatchContext)
    const [context, dispatch] = useReducer(blueConicConfigurationReducer, {
        domain,
        sourceConfig: config,
        filters: extractFilterRecordMapFromPQuery(config.filters),
        boosts: config.boosts,
        id: config.id,
        count: 1,
    })

    useEffect(() => {
        recommendationDispatch({
            type: 'update-configuration',
            recordId,
            configuration: config.clone({
                id: context.id,
                filters: parsePQueryFromFilterRecordMap(
                    context.filters,
                ) as BlueConicRecommendationConfiguration['filters'],
                boosts: context.boosts,
                count: 1,
            }),
        })
    }, [context])

    return (
        <div className="blueconic-configuration-editor">
            <BlueConicConfigurationContext.Provider value={context}>
                <BlueConicConfigurationDispatchContext.Provider value={dispatch}>
                    {!isReadonly && (
                        <OrderHandler
                            onChange={(shift) =>
                                recommendationDispatch({ type: 'shift-configuration', recordId, ordinalShift: shift })
                            }
                            hideUp={config.id === 'first'}
                            hideDown={config.id === Array.from(recommendationContext.configs.values()).pop()?.id}
                        />
                    )}
                    <div id={recordId}>
                        <Card
                            className="blueconic-configuration-editor-card"
                            key={recordId}
                            bordered={false}
                            size="small"
                            title={
                                <>
                                    <LayoutRowWithActions
                                        readonly={isReadonly}
                                        dataItem={
                                            <span>
                                                Configuration
                                                <Popover
                                                    overlayClassName="blueconic-config-header-popover"
                                                    content={
                                                        <div>
                                                            <p>
                                                                The order of execution of the defined configurations
                                                                from top to bottom: the first will be executed first,
                                                                followed by the second, and so on until the total count
                                                                has been reached.
                                                                <br /> <br />
                                                                This is the <b>{config.id}</b> configuration in the
                                                                order.
                                                            </p>
                                                        </div>
                                                    }
                                                    trigger="hover"
                                                >
                                                    <ExclamationCircleOutlined className="info-icon" />
                                                </Popover>
                                            </span>
                                        }
                                        onCopy={() => {
                                            let canDuplicate = false
                                            try {
                                                canDuplicate = !!config.validate()

                                                if (!config.boosts.length) {
                                                    canDuplicate = false
                                                    throw new Error(
                                                        'A configuration must have at least one boost configured to be duplicated',
                                                    )
                                                }
                                            } catch (error) {
                                                simpleNotification('error', error.message)
                                            }

                                            if (canDuplicate) {
                                                handleAdd(config.clone())
                                            }
                                        }}
                                        onDelete={() =>
                                            recommendationDispatch({ type: 'remove-configuration', recordId })
                                        }
                                    />

                                    {/** ID Currently Hidden and dynamically created for BlueConic Configuration Payload - consider enabling for future templating use/general exposure */}
                                    {/*<div className="configuration-id-section">*/}
                                    {/*    <Input*/}
                                    {/*        size="middle"*/}
                                    {/*        value={context.id}*/}
                                    {/*        onChange={(ev) => {*/}
                                    {/*            dispatch({ type: 'set-id', id: ev.target.value })*/}
                                    {/*        }}*/}
                                    {/*    />*/}
                                    {/*</div>*/}
                                </>
                            }
                        >
                            <div className="configuration-boosts-section">
                                <BlueConicBoostsEditor />
                            </div>

                            <BlueConicFiltersEditor />
                        </Card>
                    </div>
                </BlueConicConfigurationDispatchContext.Provider>
            </BlueConicConfigurationContext.Provider>
        </div>
    )
}
